import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Loader2Icon,
  MailCheckIcon,
  RefreshCcwIcon,
} from 'lucide-react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { AppSidebarLayout } from '@/components/app-sidebar';
import { AccountPage } from '@/components/account-page';
import { DesignSystemPage } from '@/components/design-system-page';
import { AuthShell } from '@/components/auth-shell';
import { ForgotPasswordForm } from '@/components/forgot-password-form';
import { LoginForm } from '@/components/login-form';
import { ResetPasswordForm } from '@/components/reset-password-form';
import { SetPasswordForm } from '@/components/set-password-form';
import { UserFormPage } from '@/components/user-form-page';
import { AdminUsersPage } from '@/components/users-management-page';
import { RegionsManagementPage } from '@/components/regions-management-page';
import { DashboardPage } from '@/components/dashboard-page';
import { EditRegionPage } from '@/components/edit-region-page';
import { SalonManagementPage } from '@/components/salon-management-page';
import { LeadsManagementPage } from '@/components/leads-management-page';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { apiBaseUrl, authClient, getBrowserUrl, sendVerificationEmail } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { getDefaultAuthenticatedPath } from '@/lib/navigation';
import { SentryUserScope } from '@/lib/sentry';
import './App.css';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;

// Neutral touch to force frontend-backoffice pipeline execution when needed.
// No functional impact for runtime behavior (redeploy marker).
function FullScreenLoader() {
  return (
    <main className="route-transition-enter flex min-h-screen items-center justify-center bg-background px-6">
      <Loader2Icon className="size-8 animate-spin text-primary" />
    </main>
  );
}

function DelayedSessionLoader({ isPending }: { isPending: boolean }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isPending) return;

    const timeoutId = window.setTimeout(() => {
      setIsVisible(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPending]);

  if (!isPending || !isVisible) {
    return null;
  }

  return <FullScreenLoader />;
}

function AnimatedOutlet({ context }: { context?: unknown }) {
  const location = useLocation();

  return (
    <div key={`${location.pathname}${location.search}`} className="route-transition-enter min-w-0">
      <Outlet context={context} />
    </div>
  );
}

function PublicOnlyLayout() {
  const location = useLocation();
  const sessionState = authClient.useSession();
  const canStayWhileAuthenticated = location.pathname === '/auth/verify-email' || location.pathname === '/auth/set-password';

  if (sessionState.isPending) {
    return <DelayedSessionLoader isPending={sessionState.isPending} />;
  }

  if (sessionState.data && !canStayWhileAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <AnimatedOutlet />;
}


function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionState = authClient.useSession();

  if (sessionState.isPending) {
    return <DelayedSessionLoader isPending={sessionState.isPending} />;
  }

  if (!sessionState.data) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  async function handleSignOut() {
    const result = await authClient.signOut();

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    await sessionState.refetch();
    toast.success('Session fermée.');
    navigate('/auth/login', { replace: true });
  }

  return (
    <AppSidebarLayout onSignOut={handleSignOut} session={sessionState.data}>
      <div className="min-w-0">
        <AnimatedOutlet context={{ session: sessionState.data, sessionState }} />
      </div>
    </AppSidebarLayout>
  );
}

function RequireSession({ children }: { children: (session: SessionData) => React.ReactNode }) {
  const sessionState = authClient.useSession();
  if (!sessionState.data) return null;
  return <>{children(sessionState.data)}</>;
}

function HomeRedirectPage() {
  return (
    <RequireSession>
      {(session) => <Navigate to={getDefaultAuthenticatedPath(session)} replace />}
    </RequireSession>
  );
}

function LoginPage() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/';

  useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      toast.success('Mot de passe réinitialisé. Tu peux maintenant te connecter.');
    }
  }, [searchParams]);

  return (
    <AuthShell
      badge="Connexion"
      title="Accédez à votre espace Mutuale."
      description="Gérez vos participations, configurez vos événements et analysez vos performances salons en toute simplicité."
    >
      <LoginForm redirectTo={redirectTo} />
    </AuthShell>
  );
}

function ForgotPasswordPage() {
  return (
    <AuthShell
      badge="Récupération"
      title="Mot de passe oublié ?"
      description="Saisissez votre adresse email professionnelle pour recevoir un lien de réinitialisation sécurisé."
    >
      <ForgotPasswordForm />
    </AuthShell>
  );
}

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const isValidated = searchParams.get('validated') === '1';
  const shouldPrevalidateToken = Boolean(token && !isValidated && !errorCode);
  const resetValidationCallbackUrl = useMemo(() => getBrowserUrl('/auth/reset-password?validated=1'), []);

  useEffect(() => {
    if (!shouldPrevalidateToken || !token) {
      return undefined;
    }

    const backendValidationUrl = new URL(`/api/auth/reset-password/${token}`, apiBaseUrl);
    backendValidationUrl.searchParams.set('callbackURL', resetValidationCallbackUrl);

    const timeoutId = window.setTimeout(() => {
      window.location.replace(backendValidationUrl.toString());
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [resetValidationCallbackUrl, shouldPrevalidateToken, token]);

  if (shouldPrevalidateToken) {
    return (
      <AuthShell
        badge="Validation"
        title="Vérification de sécurité en cours."
        description="Nous authentifions votre demande de réinitialisation pour garantir la protection de vos données."
      >
        <div data-testid="reset-password-prevalidation-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-sky-50 text-sky-700">
            <Loader2Icon className="size-7 animate-spin" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Validation du lien en cours</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              Nous vérifions ton lien de réinitialisation avant d afficher le formulaire.
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (errorCode) {
    return (
      <AuthShell
        badge="Lien Invalide"
        title="Le lien de réinitialisation ne peut pas être utilisé."
        description="Le token a expiré, est invalide ou a déjà été consommé."
      >
        <div data-testid="reset-password-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Lien de réinitialisation invalide</h1>
            <p data-testid="reset-password-error-message" className="text-sm leading-6 text-muted-foreground">
              {getAuthErrorMessage({ code: errorCode })}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button asChild>
              <a href="/auth/forgot-password">Demander un nouveau lien</a>
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      badge="Sécurité"
      title="Nouveau mot de passe."
      description="Votre demande a été validée. Vous pouvez maintenant définir vos nouveaux identifiants."
    >
      <ResetPasswordForm token={isValidated ? token : null} />
    </AuthShell>
  );
}

function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionState = authClient.useSession();
  const hasScheduledRedirect = useRef(false);
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const email = searchParams.get('email') ?? sessionState.data?.user.email ?? '';
  const mode = searchParams.get('mode');
  const verificationCallbackUrl = useMemo(() => {
    const query = new URLSearchParams();

    if (email) {
      query.set('email', email);
    }

    if (mode) {
      query.set('mode', mode);
    }

    const callbackPath = query.size > 0 ? `/auth/verify-email?${query.toString()}` : '/auth/verify-email';

    return getBrowserUrl(callbackPath);
  }, [email, mode]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const backendVerificationUrl = new URL('/api/auth/verify-email', apiBaseUrl);
    backendVerificationUrl.searchParams.set('token', token);
    backendVerificationUrl.searchParams.set('callbackURL', verificationCallbackUrl);

    const timeoutId = window.setTimeout(() => {
      window.location.replace(backendVerificationUrl.toString());
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [token, verificationCallbackUrl]);

  useEffect(() => {
    if (mode === 'set-password' || token || !sessionState.data?.user.emailVerified || hasScheduledRedirect.current) {
      return undefined;
    }

    hasScheduledRedirect.current = true;
    toast.success('Email vérifié. Redirection vers l application.');

    const timeoutId = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mode, navigate, sessionState.data, token]);

  async function handleResendEmail() {
    if (!email) {
      toast.error('Aucun email disponible pour renvoyer le lien.');
      return;
    }

    const query = new URLSearchParams();

    if (email) {
      query.set('email', email);
    }

    if (mode) {
      query.set('mode', mode);
    }

    const callbackPath = query.size > 0 ? `/auth/verify-email?${query.toString()}` : '/auth/verify-email';
    const result = await sendVerificationEmail(email, getBrowserUrl(callbackPath));

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien de confirmation a été envoyé.');
  }

  return (
    <AuthShell
      badge="Activation"
      title="Vérifiez votre boîte mail."
      description="Un lien de confirmation vous a été envoyé pour finaliser l'activation de votre compte Mutuale."
    >
      {token ? (
        <div data-testid="verify-email-prevalidation-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-sky-50 text-sky-700">
            <Loader2Icon className="size-7 animate-spin" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Activation du compte en cours</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Nous vérifions actuellement ${email} et préparons ta session.`
                : 'Nous vérifions actuellement ton adresse email et préparons ta session.'}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Tu vas être redirigé automatiquement dans un instant.
            </p>
          </div>
        </div>
      ) : errorCode ? (
        <div data-testid="verify-email-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Le lien de confirmation a un problème</h1>
            <p data-testid="verify-email-error-message" className="text-sm leading-6 text-muted-foreground">
              {getAuthErrorMessage({ code: errorCode })}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Tu peux demander un nouveau lien de confirmation si nécessaire.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      ) : sessionState.data?.user.emailVerified ? (
        <div data-testid="verify-email-success-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Compte activé</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {mode === 'set-password'
                ? 'Ton email est confirmé. Tu peux maintenant définir ton mot de passe.'
                : 'Ton email est confirmé. Tu peux maintenant ouvrir l application.'}
            </p>
          </div>
          {mode === 'set-password' ? (
            <div className="flex flex-col gap-3">
              <Button asChild>
                <a href="/auth/set-password">Définir mon mot de passe</a>
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Button asChild>
                <a href="/">Ouvrir l application</a>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="verify-email-idle-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Vérifie ton email</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Un lien de confirmation a été envoyé à ${email}.`
                : 'Un lien de confirmation a été envoyé sur ton adresse email.'}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Clique sur le lien reçu par email pour activer ton compte, puis cette page te redirigera automatiquement.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const sessionState = authClient.useSession();
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const email = searchParams.get('email') ?? sessionState.data?.user.email ?? '';

  async function handleResendEmail() {
    if (!email) {
      toast.error('Aucun email disponible pour renvoyer le lien.');
      return;
    }

    const result = await sendVerificationEmail(
      email,
      getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(email)}`),
    );

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien d activation a été envoyé.');
  }

  if (token) {
    return (
      <AuthShell
        badge="Invitation"
        title="Finalise ton accès."
        description="Définis maintenant ton mot de passe pour activer tes prochaines connexions à la console."
      >
        <SetPasswordForm token={token} />
      </AuthShell>
    );
  }

  if (errorCode) {
    return (
      <AuthShell
        badge="Lien Invalide"
        title="Le lien d invitation ne peut pas être utilisé."
        description="Le token a expiré, est invalide ou a déjà été consommé."
      >
        <div data-testid="set-password-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Lien d activation invalide</h1>
            <p className="text-sm leading-6 text-muted-foreground">{getAuthErrorMessage({ code: errorCode })}</p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (!sessionState.data) {
    return (
      <AuthShell
        badge="Invitation"
        title="Consulte ton email pour activer le compte."
        description="L activation de l email ouvre ensuite directement l étape de création du mot de passe."
      >
        <div data-testid="set-password-idle-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Active d abord ton compte</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Un email d activation a été envoyé à ${email}.`
                : 'Un email d activation a été envoyé sur ton adresse email.'}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      badge="Mot De Passe"
      title="Finalise ton accès."
      description="Ton email est confirmé et ta session est ouverte. Choisis maintenant le mot de passe qui servira aux prochaines connexions."
    >
      <SetPasswordForm />
    </AuthShell>
  );
}


function AccountRoutePage() {
  return <AccountPage />;
}

function ComponentsRoutePage() {
  return <DesignSystemPage />;
}

function NotFoundPage() {
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <>
      <SentryUserScope />
      <SentryRoutes>
        <Route element={<PublicOnlyLayout />}>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<Navigate to="/auth/login" replace />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
        </Route>
        <Route element={<ProtectedLayout />}>
          <Route
            path="/"
            element={<HomeRedirectPage />}
          />
          <Route
            path="/admin/dashboard"
            element={(
              <RequireSession>
                {(session) => <DashboardPage session={session} />}
              </RequireSession>
            )}
          />
          <Route path="/account" element={<AccountRoutePage />} />
          <Route path="/components" element={<ComponentsRoutePage />} />
          <Route
            path="/admin/users"
            element={(
              <RequireSession>
                {(session) => <AdminUsersPage session={session} />}
              </RequireSession>
            )}
          />
          <Route path="/admin/users/new" element={<UserFormPage />} />
          <Route path="/admin/users/:id" element={<UserFormPage />} />
          <Route
            path="/admin/regions"
            element={(
              <RequireSession>
                {(session) => <RegionsManagementPage session={session} />}
              </RequireSession>
            )}
          />
          <Route path="/admin/regions/new" element={<Navigate to="/admin/regions" replace />} />
          <Route path="/admin/regions/:id/edit" element={<EditRegionPage />} />
          <Route path="/admin/regions/:id/salons" element={<SalonManagementPage />} />
          <Route
            path="/admin/salons"
            element={(
              <RequireSession>
                {(session) => <Navigate to={getDefaultAuthenticatedPath(session)} replace />}
              </RequireSession>
            )}
          />
          <Route path="/admin/leads" element={<LeadsManagementPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </SentryRoutes>
      <Toaster position="top-center" />
    </>
  );
}

export default App;
